import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../components/ui/Badge.jsx';
import { Table } from '../components/ui/Table.jsx';
import { useTranslation } from '../i18n/useTranslation.js';
import { formatDateTime } from '../utils/formatters.js';

import { usePlatformAuth } from './PlatformAuthContext.jsx';
import { demoRequestsApi } from './platformApi.js';
import './platform.css';

const STATUS_VARIANT = {
  new: 'warning',
  seen: 'neutral',
  contacted: 'success',
  closed: 'neutral',
};

const NEXT_STATUS = ['new', 'seen', 'contacted', 'closed'];

/**
 * Posteingang für Demo-Anfragen von der öffentlichen Landing Page.
 *
 * Diese Anfragen sind Recicas eigener Vertrieb. Früher lösten sie eine Glocke
 * bei JEDEM Administrator der nutzenden Firma aus und wurden nirgends
 * angezeigt - das Repository konnte nur schreiben. Hier sind sie zum ersten Mal
 * sichtbar, und zwar ausschliesslich für den Anbieter.
 */
export function DemoRequestsPage() {
  const { t } = useTranslation();
  const { permissions } = usePlatformAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const canWrite = permissions?.demoRequests === 'write';

  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'demo-requests', search, status],
    queryFn: () => demoRequestsApi.list({ search, status }),
  });

  const rows = data?.items || [];

  const setStatusMutation = useMutation({
    mutationFn: ({ id, next }) => demoRequestsApi.setStatus(id, next),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['platform', 'demo-requests'] }),
  });

  const statusLabel = (key) => t(`platform.demo.status.${key}`);

  const columns = [
    { key: 'createdAt', label: t('platform.demo.col.received'), render: (r) => formatDateTime(r.createdAt) },
    { key: 'company', label: t('platform.demo.col.company') },
    { key: 'name', label: t('platform.demo.col.name') },
    {
      key: 'email',
      label: t('common.email'),
      render: (r) => <a href={`mailto:${r.email}`}>{r.email}</a>,
    },
    {
      key: 'message',
      label: t('platform.demo.col.message'),
      render: (r) => (r.message ? <span title={r.message}>{r.message.slice(0, 80)}{r.message.length > 80 ? '…' : ''}</span> : '-'),
    },
    {
      key: 'status',
      label: t('common.status'),
      render: (r) =>
        canWrite ? (
          <select
            className="input"
            style={{ maxWidth: 150 }}
            value={r.status}
            disabled={setStatusMutation.isPending}
            onChange={(e) => setStatusMutation.mutate({ id: r.id, next: e.target.value })}
          >
            {NEXT_STATUS.map((s) => (
              <option key={s} value={s}>{statusLabel(s)}</option>
            ))}
          </select>
        ) : (
          <Badge variant={STATUS_VARIANT[r.status] || 'neutral'}>{statusLabel(r.status)}</Badge>
        ),
    },
  ];

  return (
    <>
      <div className="pf-page-head">
        <div>
          <h1>{t('platform.demo.title')}</h1>
          <p>{t('platform.demo.subtitle')}</p>
        </div>
      </div>

      <div className="pf-filters">
        <input
          className="input"
          style={{ maxWidth: 280 }}
          placeholder={t('platform.demo.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="input" style={{ maxWidth: 200 }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t('platform.demo.status.all')}</option>
          {NEXT_STATUS.map((s) => (
            <option key={s} value={s}>{statusLabel(s)}</option>
          ))}
        </select>
        <span className="muted text-xs">
          {t('platform.demo.count', { count: data?.meta?.total ?? rows.length })}
        </span>
      </div>

      <Table
        columns={columns}
        rows={rows}
        isLoading={isLoading}
        emptyMessage={t('platform.demo.empty')}
      />
    </>
  );
}
